import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import DashboardRenderer from './components/Dashboard/DashboardRenderer'
import AgentChat from './components/Agent/AgentChat'
import Login from './pages/Login'
import ProtectedRoute from './components/ProtectedRoute'
import { ChartDataProvider, useChartData } from './contexts/ChartDataContext'
import { AgentProvider } from './contexts/AgentContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { getTenantConfig } from './services/configService'
import useIdleTimeout from './hooks/useIdleTimeout'
import './App.css'

function AppContent() {
  const [currentView, setCurrentView] = useState('dashboard')
  const [tenantConfig, setTenantConfig] = useState({
    app_title: 'Calon Pulse (Beta)' // Loading state
  })
  const { getLastUpdatedTime } = useChartData()
  const { user, logout } = useAuth()

  // Bug #021: Idle timeout (15 minutes)
  useIdleTimeout(15 * 60 * 1000)

  // Load tenant config on mount
  useEffect(() => {
    const loadTenantConfig = async () => {
      const config = await getTenantConfig()
      setTenantConfig(config)
    }
    loadTenantConfig()
  }, [])

  // Format last updated timestamp
  const formatLastUpdated = () => {
    const timestamp = getLastUpdatedTime()
    if (!timestamp) return 'Loading data...'

    const date = new Date(timestamp)
    return `Last updated ${date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`
  }

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-top">
          <div className="header-left">
            <h1>{tenantConfig.app_title}</h1>
            <p className="subtitle">{formatLastUpdated()}</p>
          </div>
          <div className="header-right">
            {user && (
              <div className="user-info">
                <span className="user-name">Welcome, {user.name}</span>
                <button className="logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
        <nav className="app-nav">
          <button
            className={currentView === 'dashboard' ? 'nav-btn active' : 'nav-btn'}
            onClick={() => setCurrentView('dashboard')}
          >
            Dashboards
          </button>
          <button
            className={currentView === 'agent' ? 'nav-btn active' : 'nav-btn'}
            onClick={() => setCurrentView('agent')}
          >
            AI Agent
          </button>
        </nav>
      </header>

      <main className="app-main">
        {currentView === 'dashboard' && <DashboardRenderer />}
        {currentView === 'agent' && <AgentChat />}
      </main>

      <footer className="app-footer">
        <p>&copy; {new Date().getFullYear()} Calon GmbH | Powered by React + FastAPI</p>
      </footer>
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChartDataProvider>
          <AgentProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AgentProvider>
        </ChartDataProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
